import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, Collapse, Grid, IconButton,
  Link, MenuItem, Paper, TextField, Tooltip, Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { frFR } from '@mui/x-data-grid/locales';
import {
  Download as DownloadIcon, FilterAltOffOutlined as FilterAltOffIcon,
  FilterAltOutlined as FilterAltIcon, PersonSearchOutlined as PersonDetailIcon,
  RestartAlt as RestartAltIcon, Search as SearchIcon,
} from '@mui/icons-material';
import PartnerMainContainer from '../../../components/PartnerMainContainer';
import LoadingSimpleBackdrop from '../../tools/LoadingSimpleBackdrop';
import TitlePartner from '../component/Title';
import PartnerEvalution from '../Parterns/PartnerEvalution';
import PersonalDetailsDialog from './PersonalDetailsDialog';
import ExportContainer from '../Export/ExportContainer';
import PersonalService from '../../../services/PersonalService';
import { exporterTableauPartenaires } from '../Export/exporterTableauPartenaires';
import {
  createPartnerIdAssigner, createPersonnelIdAssigner, formatDate,
  formatPartnerSource, getPersonnelMatchKey, getSituationChipSx,
  isValidValue, normalizeSituation, normalizeText,
} from '../../../helpers/PartnerHelper';
import usePartnerFilterConsultation from '../hooks/usePartnerFilterConsultation';
import {
  partnerDataGridSx, partnerFilterButtonSx, partnerFilterFieldSx,
  partnerTableColors,
} from '../Parterns/partnerListTheme';

const normalizeRows = (rawRows) => {
  const getPersonnelId = createPersonnelIdAssigner();
  const getPartnerId = createPartnerIdAssigner();
  const rowsByAffectation = new Map();

  (Array.isArray(rawRows) ? rawRows : []).forEach((row, index) => {
    if (!isValidValue(row.nomPersonnel)) return;
    const nomPersonnel = String(row.nomPersonnel).trim();
    const cin = isValidValue(row.cin) ? String(row.cin).trim() : '';
    const partenaire = isValidValue(row.partenaire) ? String(row.partenaire).trim() : '—';
    const personKey = isValidValue(row.personKey)
      ? String(row.personKey).trim()
      : getPersonnelMatchKey(nomPersonnel, cin);
    const affectationId = Number(row.affectation_personnel_id) || null;
    const executionId = Number(row.execution_marche_id) || null;
    const key = affectationId ? `AFF:${affectationId}` : `${personKey}:${executionId || index}`;
    if (rowsByAffectation.has(key)) return;

    rowsByAffectation.set(key, {
      id: key,
      affectationPersonnelId: affectationId,
      executionMarcheId: executionId,
      id_marche_spm: Number(row.id_marche_spm) || null,
      partenaire_id: Number(row.partenaire_id) || null,
      personnel_id: Number(row.personnel_id) || null,
      nomPersonnel,
      cin,
      fonction: isValidValue(row.fonction) ? String(row.fonction).trim() : '—',
      telephonePersonnel: isValidValue(row.telephonePersonnel) ? String(row.telephonePersonnel).trim() : '',
      emailPersonnel: isValidValue(row.emailPersonnel) ? String(row.emailPersonnel).trim() : '',
      adresse: isValidValue(row.adresse) ? String(row.adresse).trim() : '',
      dateNaissance: isValidValue(row.dateNaissance) ? String(row.dateNaissance).trim() : '',
      diplome: isValidValue(row.diplome) ? String(row.diplome).trim() : '',
      filiere: isValidValue(row.filiere) ? String(row.filiere).trim() : '',
      partenaire,
      partnerId: row.partenaire_id ? `P-${String(row.partenaire_id).padStart(5, '0')}` : getPartnerId(partenaire),
      direction: isValidValue(row.direction) ? String(row.direction).trim() : '—',
      dateDebut: row.dateDebut || '',
      dateFin: row.dateFin || '',
      numeroMarche: row.numeroMarche || row.ref_num_contrat || '',
      financement: row.financement || '',
      composante: row.composante || '',
      objet: row.objet || '',
      situation: normalizeSituation(row.situation) || '—',
      situationPersonnel: row.situationPersonnel || '',
      dateDebutContratPersonnel: row.dateDebutContratPersonnel || '',
      dateFinContratPersonnel: row.dateFinContratPersonnel || '',
      causeRupturePersonnel: row.causeRupturePersonnel || '',
      source_db: row.source_db || 'bdd partenaire',
      personKey,
      personId: row.personnel_id
        ? `PE-${String(row.personnel_id).padStart(5, '0')}`
        : row._peid || getPersonnelId(nomPersonnel, cin),
      searchName: normalizeText(nomPersonnel),
      searchCin: normalizeText(cin),
      searchPartner: normalizeText(partenaire),
    });
  });

  const normalizedRows = Array.from(rowsByAffectation.values());
  const partnersByPerson = new Map();
  normalizedRows.forEach((row) => {
    if (!partnersByPerson.has(row.personKey)) partnersByPerson.set(row.personKey, new Set());
    if (row.partenaire !== '—') partnersByPerson.get(row.personKey).add(row.partenaire);
  });
  return normalizedRows.map((row) => ({
    ...row,
    isMulti: (partnersByPerson.get(row.personKey)?.size || 0) > 1,
  }));
};


const columnGroupingModel = [
  {
    groupId: 'personnel',
    headerName: 'CONCERNANT LE PERSONNEL',
    headerClassName: 'personnel-column-group',
    children: [
      { field: 'nomPersonnel' }, { field: 'cin' },
      { field: 'fonction' }, { field: 'telephonePersonnel' },
    ],
  },
  {
    groupId: 'partenaire',
    headerName: 'CONCERNANT LE PARTENAIRE',
    headerClassName: 'partner-column-group',
    children: [
      { field: 'partenaire' }, { field: 'direction' },
      { field: 'dateDebut' }, { field: 'dateFin' }, { field: 'situation' },
      { field: 'source_db' }, { field: 'fiche' },
    ],
  },
];

const Personal = () => {
  const [rawRows, setRawRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [detailError, setDetailError] = useState('');
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedPartnerName, setSelectedPartnerName] = useState('');
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);
  const [selectedPartnerPid, setSelectedPartnerPid] = useState('');
  const [selectedPartnerSourceDb, setSelectedPartnerSourceDb] = useState('');
  const [isPartnerDetailOpen, setIsPartnerDetailOpen] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [selectedPersonRows, setSelectedPersonRows] = useState([]);

  const defaultFilters = useMemo(() => ({
    search: '', partner: '', directions: [], situation: '',
    sortBy: 'nomPersonnel', dateFrom: '', dateTo: '',
  }), []);
  const [filters, setFilters] = useState(defaultFilters);

  usePartnerFilterConsultation(filters, (nextFilters) => PersonalService.getAllRows(nextFilters));

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    PersonalService.getAllRows().then((result) => {
      if (!active) return;
      const data = Array.isArray(result?.data) ? result.data : [];
      const message = typeof result?.message === 'string' ? result.message.trim() : '';
      setRawRows(data);
      setLoadError(result?.status === 0 && !data.length && !/^aucun/i.test(message) ? message : '');
    }).catch(() => {
      if (active) setLoadError('Impossible de charger le registre du personnel.');
    }).finally(() => {
      if (active) setIsLoading(false);
    });
    return () => { active = false; };
  }, []);

  const baseRows = useMemo(() => normalizeRows(rawRows), [rawRows]);
  const directionOptions = useMemo(() => [...new Set(baseRows
    .map((row) => row.direction).filter((value) => value !== '—'))]
    .sort((a, b) => a.localeCompare(b, 'fr')), [baseRows]);

  const rows = useMemo(() => baseRows.filter((row) => {
    const search = normalizeText(filters.search);
    if (search && !row.searchName.includes(search) && !row.searchCin.includes(search)) return false;
    if (filters.partner && !row.searchPartner.includes(normalizeText(filters.partner))) return false;
    if (filters.directions.length && !filters.directions.includes(row.direction)) return false;
    if (filters.situation && row.situation !== filters.situation) return false;
    if (filters.dateFrom && row.dateFin && row.dateFin < filters.dateFrom) return false;
    if (filters.dateTo && row.dateDebut && row.dateDebut > filters.dateTo) return false;
    return true;
  }).sort((left, right) => String(left[filters.sortBy] || '')
    .localeCompare(String(right[filters.sortBy] || ''), 'fr')), [baseRows, filters]);

  useEffect(() => setPage(0), [filters]);

  const activeFiltersCount = (filters.search ? 1 : 0) + (filters.partner ? 1 : 0)
    + filters.directions.length + (filters.situation ? 1 : 0)
    + (filters.dateFrom ? 1 : 0) + (filters.dateTo ? 1 : 0);

  const handleOpenPartnerDetail = (partner) => {
    const partnerName = typeof partner === 'string' ? partner : partner?.partenaire;
    if (!isValidValue(partnerName) || partnerName === '—') return;
    setSelectedPartnerName(partnerName);
    setSelectedPartnerId(typeof partner === 'object' ? partner.partenaire_id || null : null);
    setSelectedPartnerPid(typeof partner === 'object' ? partner.partnerId || '' : '');
    setSelectedPartnerSourceDb(typeof partner === 'object' ? partner.source_db || '' : '');
    setIsPartnerDetailOpen(true);
  };

  const handleOpenPersonDetail = async (row) => {
    if (!row) return;
    setDetailError('');
    if (!row.personnel_id) {
      setSelectedPerson(row);
      setSelectedPersonRows(baseRows.filter((item) => item.personKey === row.personKey));
      return;
    }

    setIsDetailLoading(true);
    try {
      const result = await PersonalService.getDetail(row.personnel_id);
      if (!result?.status || !Array.isArray(result.data) || !result.data.length) {
        setDetailError(result?.message || 'Impossible de charger la fiche personnel.');
        return;
      }
      const detailRows = normalizeRows(result.data);
      setSelectedPerson(detailRows[0] || row);
      setSelectedPersonRows(detailRows);
    } catch (error) {
      setDetailError('Impossible de charger la fiche personnel.');
    } finally {
      setIsDetailLoading(false);
    }
  };

  const columns = [
    { field: 'nomPersonnel', headerName: 'Nom personnel', minWidth: 190, flex: 1.1, renderCell: ({ row, value }) => <Box><Typography sx={{ fontSize: 12, fontWeight: 700 }}>{value}</Typography><StackBadges row={row} /></Box> },
    { field: 'cin', headerName: 'CIN', minWidth: 125, flex: 0.7, renderCell: ({ value }) => value || '—' },
    { field: 'fonction', headerName: 'Fonction', minWidth: 145, flex: 0.8 },
    { field: 'telephonePersonnel', headerName: 'Téléphone', minWidth: 125, flex: 0.7, renderCell: ({ value }) => value || '—' },
    { field: 'partenaire', headerName: 'Partenaire', minWidth: 220, flex: 1.2, renderCell: ({ row, value }) => value === '—' ? '—' : <Box sx={{ minWidth: 0 }}><Link component="button" type="button" underline="hover" onClick={(event) => { event.stopPropagation(); handleOpenPartnerDetail(row); }} onDoubleClick={(event) => event.stopPropagation()} sx={{ color: partnerTableColors.dark, display: 'block', fontSize: 12, fontWeight: 700, lineHeight: 1.2, maxWidth: '100%', overflow: 'hidden', textAlign: 'left', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</Link><Typography sx={{ color: '#667085', fontSize: 10, lineHeight: 1.2, mt: 0.25 }}>{row.partnerId || '—'}</Typography></Box> },
    { field: 'direction', headerName: 'Direction', minWidth: 105, flex: 0.6 },
    { field: 'dateDebut', headerName: 'Début', minWidth: 105, renderCell: ({ value }) => formatDate(value) },
    { field: 'dateFin', headerName: 'Fin', minWidth: 105, renderCell: ({ value }) => formatDate(value) },
    { field: 'situation', headerName: 'Situation', minWidth: 125, renderCell: ({ value }) => <Chip size="small" label={value || '—'} sx={getSituationChipSx(value)} /> },
    { field: 'source_db', headerName: 'Base source', minWidth: 175, flex: 0.9, renderCell: ({ value }) => formatPartnerSource(value) },
    { field: 'fiche', headerName: 'Fiche', width: 75, sortable: false, filterable: false, align: 'center', headerAlign: 'center', renderCell: ({ row }) => <Tooltip title="Fiche personnel"><IconButton size="small" onClick={(event) => { event.stopPropagation(); handleOpenPersonDetail(row); }} onDoubleClick={(event) => event.stopPropagation()}><PersonDetailIcon fontSize="small" /></IconButton></Tooltip> },
  ];

  const exportRows = rows.map((row) => ({
    'ID Personnel': row.personId,
    Nom: row.nomPersonnel,
    CIN: row.cin,
    Fonction: row.fonction,
    Téléphone: row.telephonePersonnel,
    'ID Partenaire': row.partnerId,
    Partenaire: row.partenaire,
    'N° marché': row.numeroMarche,
    Direction: row.direction,
    Financement: row.financement,
    Composante: row.composante,
    Début: formatDate(row.dateDebut),
    Fin: formatDate(row.dateFin),
    Situation: row.situation,
    'Base source': formatPartnerSource(row.source_db),
    'Multi-partenaire': row.isMulti ? 'OUI' : '',
  }));
  const totals = {
    count: rows.length,
    uniquePersonnel: new Set(rows.map((row) => row.personnel_id || row.personKey)).size,
    multiCount: rows.filter((row) => row.isMulti).length,
  };
  const fileName = `personnel_${new Date().toISOString().split('T')[0]}`;
  const exporter = (format) => exporterTableauPartenaires({
    format, lignes: exportRows,
    entetes: ['ID Personnel', 'Nom', 'CIN', 'Fonction', 'Téléphone', 'ID Partenaire',
      'Partenaire', 'N° marché', 'Direction', 'Financement', 'Composante', 'Début', 'Fin',
      'Situation', 'Base source', 'Multi-partenaire'],
    titre: 'Rapport du personnel', nomFichier: fileName, nomFeuille: 'Données',
    resume: `${totals.count} ligne(s) · ${totals.uniquePersonnel} personnel(s) unique(s) · ${totals.multiCount} ligne(s) multi-partenaires`,
    rapportExcel: ({ colonnes, valeurs }) => [
      ['RAPPORT DU PERSONNEL'], ['Date d’exportation', new Date().toLocaleString('fr-FR')],
      ['Nombre de lignes', totals.count], ['Personnel unique', totals.uniquePersonnel],
      ['Lignes multi-partenaires', totals.multiCount], [], colonnes, ...valeurs,
    ],
    pdf: { taillePolice: 6.5 },
    audit: { menu: 'personal', format, filters, rowCount: rows.length },
  });
  const handleExcelExport = () => exporter('xlsx');
  const handleCsvExport = () => exporter('csv');
  const handlePdfExport = () => exporter('pdf');
  const handlePrint = () => exporter('print');

  return <PartnerMainContainer>
    <LoadingSimpleBackdrop openLoadingSimpleBackdrop={isLoading || isDetailLoading} />
    <TitlePartner title="Personnel (controle)" sub={`(${rows.length} lignes)`} icon="👥" />
    {loadError && <Alert severity="error" sx={{ mb: 1.5 }}>{loadError}</Alert>}
    {detailError && <Alert severity="error" onClose={() => setDetailError('')} sx={{ mb: 1.5 }}>{detailError}</Alert>}

    <Paper elevation={0} sx={{ border: '1px solid #cbd8e6', borderRadius: '8px', mb: 2, p: 1.25 }}>
      <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, justifyContent: 'space-between', mb: filtersVisible ? 1.5 : 0 }}>
        <Box><Typography color="#52677c" fontSize={10} fontWeight={700} letterSpacing="0.05em">FILTRES</Typography><Typography color="#667085" fontSize={10}>{activeFiltersCount} critère(s) actif(s)</Typography></Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          {filtersVisible && <Button disabled={!activeFiltersCount} onClick={() => setFilters(defaultFilters)} size="small" startIcon={<RestartAltIcon />} sx={{ ...partnerFilterButtonSx, borderColor: '#9fb1c3', color: partnerTableColors.dark }} variant="outlined">Réinitialiser</Button>}
          <Button onClick={() => setFiltersVisible((value) => !value)} size="small" startIcon={filtersVisible ? <FilterAltOffIcon /> : <FilterAltIcon />} sx={{ ...partnerFilterButtonSx, borderColor: '#9fb1c3', color: partnerTableColors.dark }} variant="outlined">{filtersVisible ? 'Masquer filtres' : 'Afficher filtres'}</Button>
          <Button disabled={!rows.length} onClick={(event) => setExportAnchorEl(event.currentTarget)} size="small" startIcon={<DownloadIcon />} sx={{ ...partnerFilterButtonSx, bgcolor: partnerTableColors.gold, color: partnerTableColors.text, fontWeight: 800, '&:hover': { bgcolor: '#c39b2e' } }} variant="contained">Exporter</Button>
        </Box>
      </Box>
      <Collapse in={filtersVisible} timeout="auto" unmountOnExit>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Grid container spacing={1.25}>
            <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" placeholder="Nom ou CIN..." value={filters.search} onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))} sx={partnerFilterFieldSx} InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: '#667085' }} /> }} /></Grid>
            <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" placeholder="Partenaire..." value={filters.partner} onChange={(event) => setFilters((current) => ({ ...current, partner: event.target.value }))} sx={partnerFilterFieldSx} /></Grid>
            <Grid item xs={12} sm={6} md={3}><Autocomplete multiple disableCloseOnSelect size="small" options={directionOptions} value={filters.directions} onChange={(_, value) => setFilters((current) => ({ ...current, directions: value }))} renderInput={(params) => <TextField {...params} label="Direction" sx={partnerFilterFieldSx} />} /></Grid>
            <Grid item xs={12} sm={6} md={3}><TextField select fullWidth label="Situation" size="small" value={filters.situation} onChange={(event) => setFilters((current) => ({ ...current, situation: event.target.value }))} sx={partnerFilterFieldSx}><MenuItem value="">Toutes</MenuItem><MenuItem value="EN COURS">EN COURS</MenuItem><MenuItem value="ACHEVÉ">ACHEVÉ</MenuItem><MenuItem value="RESILIE">RESILIE</MenuItem></TextField></Grid>
          </Grid>
          <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1.25 }}>
            <TextField select label="Trier par" size="small" value={filters.sortBy} onChange={(event) => setFilters((current) => ({ ...current, sortBy: event.target.value }))} sx={{ ...partnerFilterFieldSx, width: { xs: '100%', md: 190 } }}><MenuItem value="nomPersonnel">Nom</MenuItem><MenuItem value="partenaire">Partenaire</MenuItem><MenuItem value="fonction">Fonction</MenuItem><MenuItem value="direction">Direction</MenuItem></TextField>
            <Box sx={{ alignItems: 'center', border: `1px solid ${partnerTableColors.border}`, borderRadius: '6px', display: 'flex', flex: 1, flexWrap: 'wrap', gap: 1, p: 1, width: '100%' }}><Typography color="#52677c" fontSize={11} fontWeight={700}>Période du contrat :</Typography><TextField type="date" size="small" value={filters.dateFrom} onChange={(event) => setFilters((current) => ({ ...current, dateFrom: event.target.value }))} sx={{ ...partnerFilterFieldSx, width: { xs: '100%', sm: 155 } }} /><Typography>→</Typography><TextField type="date" size="small" value={filters.dateTo} onChange={(event) => setFilters((current) => ({ ...current, dateTo: event.target.value }))} sx={{ ...partnerFilterFieldSx, width: { xs: '100%', sm: 155 } }} /></Box>
          </Box>
        </Box>
      </Collapse>
    </Paper>

    <Paper elevation={0} sx={{ border: '1px solid #cbd8e6', borderRadius: '8px', overflow: 'hidden' }}>
      <Box sx={{ alignItems: 'center', bgcolor: '#f8fbff', borderBottom: `1px solid ${partnerTableColors.border}`, display: 'flex', justifyContent: 'space-between', px: 1.75, py: 1.5 }}><Typography sx={{ color: '#1f2a44', fontSize: 13, fontWeight: 600 }}>Liste du personnel — 1 ligne = 1 affectation</Typography><Typography sx={{ color: '#4b6488', fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{rows.length} ligne(s)</Typography></Box>
      <Box sx={{ height: 560 }}><DataGrid rows={rows} columns={columns} columnGroupingModel={columnGroupingModel} columnGroupHeaderHeight={40} columnHeaderHeight={44} disableRowSelectionOnClick localeText={frFR.components.MuiDataGrid.defaultProps.localeText} onPaginationModelChange={(model) => { setPage(model.page); setPageSize(model.pageSize); }} onRowDoubleClick={({ row }) => handleOpenPersonDetail(row)} pageSizeOptions={[10, 15, 30, 50]} pagination paginationModel={{ page, pageSize }} rowHeight={48} sx={{ ...partnerDataGridSx, '& .personnel-column-group, & .partner-column-group': { backgroundColor: '#fff !important', borderBottom: `1px solid ${partnerTableColors.border}`, color: partnerTableColors.text, fontSize: 12, fontWeight: 800 }, '& .personnel-column-group': { borderRight: '4px solid #dbe8f6' } }} /></Box>
    </Paper>

    <PartnerEvalution open={isPartnerDetailOpen} onClose={() => setIsPartnerDetailOpen(false)} partnerId={selectedPartnerId} partnerPid={selectedPartnerPid} partnerName={selectedPartnerName} sourceDb={selectedPartnerSourceDb} />
    <PersonalDetailsDialog open={Boolean(selectedPerson)} person={selectedPerson} rows={selectedPersonRows} onClose={() => { setSelectedPerson(null); setSelectedPersonRows([]); }} onPartnerOpen={handleOpenPartnerDetail} />
    <ExportContainer exportAnchorEl={exportAnchorEl} handleExportClose={() => setExportAnchorEl(null)} handlePrint={handlePrint} handleExcelExport={handleExcelExport} handleCsvExport={handleCsvExport} handlePdfExport={handlePdfExport} />
  </PartnerMainContainer>;
};

const StackBadges = ({ row }) => <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
  {row.personId && <Typography sx={{ color: '#667085', fontSize: 10 }}>{row.personId}</Typography>}
  {row.isMulti && <Chip label="MULTI" size="small" sx={{ height: 16, fontSize: 8, fontWeight: 800 }} />}
</Box>;

export default Personal;
